import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateSavingsGoalContributionRequest,
  CreateSavingsGoalRequest,
  ManualSavingsGoalCorrectionRequest,
  SavingsGoalView,
  UpdateSavingsGoalRequest,
} from '@financial/shared';
import { api } from '../../lib/api';
import { notify } from '../../store/notifications';

const KEY = ['savings-goals'];

export function useSavings() {
  return useQuery({
    queryKey: KEY,
    queryFn: async (): Promise<SavingsGoalView[]> => {
      const res = await api.get<SavingsGoalView[]>('/savings-goals');
      return res.data;
    },
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: KEY });
    void qc.invalidateQueries({ queryKey: ['insights'] });
    void qc.invalidateQueries({ queryKey: ['accounts'] });
    void qc.invalidateQueries({ queryKey: ['net-worth'] });
    void qc.invalidateQueries({ queryKey: ['transfers'] });
    void qc.invalidateQueries({ queryKey: ['budget'] });
  };
}

export function useCreateGoal() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (body: CreateSavingsGoalRequest) => {
      const res = await api.post<SavingsGoalView>('/savings-goals', body);
      return res.data;
    },
    onSuccess: (goal) => {
      invalidate();
      notify.success('Meta creada', {
        action: {
          label: 'Deshacer',
          onClick: async () => {
            await api.delete(`/savings-goals/${goal.id}`);
            invalidate();
            notify.info('Meta deshecha');
          },
        },
      });
    },
  });
}

export function useUpdateGoal() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (vars: { id: string; body: UpdateSavingsGoalRequest }) => {
      const res = await api.patch<SavingsGoalView>(`/savings-goals/${vars.id}`, vars.body);
      return res.data;
    },
    onSuccess: () => {
      invalidate();
      notify.success('Meta actualizada');
    },
  });
}

/** Registra una aportación que mueve dinero real entre cuentas/apartados. */
export function useContributeMoney() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (vars: { id: string; body: CreateSavingsGoalContributionRequest }) => {
      const res = await api.post<SavingsGoalView>(
        `/savings-goals/${vars.id}/contributions`,
        vars.body,
      );
      return res.data;
    },
    onSuccess: invalidate,
  });
}

/** Ajuste excepcional: cambia el avance mostrado, pero no mueve dinero. */
export function useManualGoalCorrection() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (vars: { id: string; body: ManualSavingsGoalCorrectionRequest }) => {
      const res = await api.post<SavingsGoalView>(
        `/savings-goals/${vars.id}/manual-correction`,
        vars.body,
      );
      return res.data;
    },
    onSuccess: invalidate,
  });
}

export function useDeleteGoal() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/savings-goals/${id}`);
    },
    onSuccess: invalidate,
  });
}
